// ════════════════════════════════════════════════════════════════════
// src/storeVisits/elcShiftEmbed.js
//
// Read-only ELC Shift data widgets for Section 1 (shiftCompliance) of the
// detailed SVR form. Each widget fetches live data for the selected store
// and renders a compact summary the AM rates against (the RAG selector
// itself is rendered by form-detailed.js, not here).
//
// 4 widgets:
//   rota         — current week's shifts for the store
//   checklists   — checklist completion this week
//   clockIns     — today's clock-in status
//   toilBalances — staff TOIL balances at the store
//
// Built against the REAL existing endpoints (shapes verified against
// worker.js, 20 May 2026):
//   GET /api/shifts?week=<store week start>&store=<name>   (v136.12: Sunday in Qatar, Monday in the UAE)
//   GET /api/checklists?store=<name>&from=<store week start>&to=<today>
//   GET /api/clock?store=<name>&date=<today>
//   GET /api/balances?year=<YYYY>          (returns ALL staff — filter client-side)
//   GET /api/staff                          (active staff, for name mapping)
//
// ⚠️ NEEDS BROWSER VERIFICATION: exact column names in each row come from
//    project knowledge, not live response inspection. If a field shows
//    "undefined", check the actual response in DevTools Network tab and
//    adjust the field name. Likely candidates flagged with NOTE comments.
//
// Re: `DB` reference — bare DB (db.js uses let/const, not on window).
// ════════════════════════════════════════════════════════════════════

// ──────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────

import { adminToken } from '../shared/admin-token.js';   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
import { isQatarCountry } from '../shared/clock-rules.js';   // v136.12: the store's country decides its week

function getToken() {
  return adminToken();   // v136.12: DB token, then this tab, then the remembered copy (src/shared/admin-token.js)
}

function getWorkerUrl() {
  if (typeof DB !== 'undefined' && typeof DB.workerUrl === 'function') {
    return DB.workerUrl();
  }
  return '';
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Dubai (UTC+4) today as YYYY-MM-DD. en-CA locale gives ISO date format.
// This is timezone-correct (not toISOString, which would drift in the evening).
function dubaiToday() {
  return new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Dubai' });
}

function pad2(n) { return String(n).padStart(2, '0'); }
function ymd(d) {
  return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate());
}

// v136.12: the store's OWN week. Qatar rotas (and the staff app's Checks screen) start the week on SUNDAY, the UAE
// on Monday (field-lessons, "The Monday assumption"); this used to be Monday for every store, so a Qatar visit
// report read the wrong rota week and lost Sunday's checklists. The country comes from the page's own
// getStoreCountry (admin.html inline, the staff app's helpers bundle), with admin's own fallback for a store that
// has no country set (a name starting "Doha").
function isQatarVisitStore(store) {
  let country = '';
  try { if (typeof getStoreCountry === 'function') country = getStoreCountry(store) || ''; } catch (e) { country = ''; }
  if (country) return isQatarCountry(country);
  return /^Doha/i.test(String(store || ''));
}

// The first day of the week holding todayStr ('YYYY-MM-DD'): Sunday for Qatar, Monday for the UAE. Pure.
function storeWeekStart(todayStr, qatar) {
  const d = new Date(String(todayStr) + 'T00:00:00Z');
  const day = d.getUTCDay();   // 0 Sunday .. 6 Saturday
  const back = qatar ? day : (day + 6) % 7;
  d.setUTCDate(d.getUTCDate() - back);
  return ymd(d);
}

function currentWeekStart(store) {
  return storeWeekStart(dubaiToday(), isQatarVisitStore(store));
}

// v136.12: one line per active checklist template for the week. Submitted rows carry the template's TYPE code
// ('opening'); the old match used the template's NAME ("Opening Checklist"), so every line read "0 day(s) this
// week" in red. Matched on t.type now, shown as t.name (else the type). Daily templates count the days done;
// weekly ones read done or not done for the week (the staff app's Checks screen asks for a weekly check once a
// week); monthly ones the same, but not red when not done, since the month's check may not fall in this week.
// A type submitted with no template (a template since deleted) still shows its days. Pure: returns
// [{ label, status, state }] with state 'ok' | 'bad' | 'neutral'.
function checklistWeekLines(templates, rows) {
  const daysByType = {};
  (Array.isArray(rows) ? rows : []).forEach(function (c) {
    if (!c || !c.type) return;
    if (!daysByType[c.type]) daysByType[c.type] = new Set();
    daysByType[c.type].add(c.date);
  });
  const active = (Array.isArray(templates) ? templates : []).filter(function (t) { return t && t.type && t.active !== false; })
    .slice().sort(function (a, b) {
      const oa = a.sort_order || 0, ob = b.sort_order || 0;
      if (oa !== ob) return oa - ob;
      return String(a.name || a.type).localeCompare(String(b.name || b.type));
    });
  const seen = {};
  const lines = active.map(function (t) {
    seen[t.type] = true;
    const n = daysByType[t.type] ? daysByType[t.type].size : 0;
    const freq = String(t.frequency || 'daily').toLowerCase();
    const label = t.name || t.type;
    if (freq === 'weekly' || freq === 'monthly') {
      if (n > 0) return { label: label, status: 'Done this week', state: 'ok' };
      return { label: label, status: 'Not done this week', state: freq === 'weekly' ? 'bad' : 'neutral' };
    }
    return { label: label, status: n + ' day(s) this week', state: n > 0 ? 'ok' : 'bad' };
  });
  Object.keys(daysByType).sort().forEach(function (type) {
    if (seen[type]) return;
    lines.push({ label: type, status: daysByType[type].size + ' day(s) this week', state: 'neutral' });
  });
  return lines;
}

async function apiGet(pathAndQuery) {
  const res = await fetch(getWorkerUrl() + pathAndQuery, {
    headers: { 'Authorization': 'Bearer ' + getToken() },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(res.status + ' ' + text.slice(0, 120));
  }
  return res.json();
}

// Cache the staff list for the duration of the form session (fetched once).
let _staffCache = null;
async function getStaff() {
  if (_staffCache) return _staffCache;
  _staffCache = await apiGet('/api/staff');
  return _staffCache;
}

function summaryBox(innerHtml) {
  return (
    '<div style="padding:12px 16px;background:var(--surface-2);border:1px solid var(--border);border-radius:8px;font-size:14px;">' +
      innerHtml +
    '</div>'
  );
}

function errorBox(msg) {
  return (
    '<div style="padding:12px 16px;background:var(--danger-bg);border:1px solid var(--learn-bad-edge);border-radius:8px;font-size:14px;color:var(--danger-deep);">' +
      '⚠️ Could not load: ' + escapeHtml(msg) +
    '</div>'
  );
}

function loadingBox() {
  return '<div style="padding:12px 16px;color:var(--tiny);font-size:14px;">Loading…</div>';
}


// ──────────────────────────────────────────────────────────────────
// Widget: rota — current week's shifts for the store
// ──────────────────────────────────────────────────────────────────

async function renderRotaEmbed(store, container) {
  container.innerHTML = loadingBox();
  try {
    const week = currentWeekStart(store);   // v136.12: Qatar rotas are filed under a Sunday week_start
    const [shifts, staff] = await Promise.all([
      apiGet('/api/shifts?week=' + encodeURIComponent(week) + '&store=' + encodeURIComponent(store)),
      getStaff(),
    ]);

    const staffById = {};
    staff.forEach(function (s) { staffById[s.id] = s; });

    // Count distinct staff with at least one shift this week
    const staffWithShifts = new Set();
    let workingShifts = 0;
    (shifts || []).forEach(function (sh) {
      staffWithShifts.add(sh.staff_id);
      // NOTE: shift type field — 'type' per schema. Working shift = not holiday/sick/toil/off.
      const t = (sh.type || '').toLowerCase();
      if (!['holiday', 'sick', 'toil', 'off', 'leave', 'apl'].includes(t)) workingShifts++;   // v129.18: apl = on leave, not cover
    });

    const rows = Array.from(staffWithShifts).map(function (id) {
      const s = staffById[id];
      return '<li style="margin:2px 0;">' + escapeHtml(s ? s.name : ('staff #' + id)) + '</li>';
    }).join('');

    container.innerHTML = summaryBox(
      '<strong>Week of ' + escapeHtml(week) + '</strong><br>' +
      (shifts && shifts.length
        ? staffWithShifts.size + ' staff rostered, ' + workingShifts + ' working shifts.' +
          '<ul style="margin:8px 0 0;padding-left:20px;">' + rows + '</ul>'
        : '<span style="color:var(--ui-bad);">No shifts found for this store this week.</span>')
    );
  } catch (e) {
    container.innerHTML = errorBox(e.message);
  }
}


// ──────────────────────────────────────────────────────────────────
// Widget: checklists — completion this week
// ──────────────────────────────────────────────────────────────────

async function renderChecklistsEmbed(store, container) {
  container.innerHTML = loadingBox();
  try {
    const week = currentWeekStart(store);   // v136.12: the store's own week (Sunday in Qatar)
    const today = dubaiToday();
    const [checklists, templates] = await Promise.all([
      apiGet('/api/checklists?store=' + encodeURIComponent(store) + '&from=' + week + '&to=' + today),
      apiGet('/api/checklist-templates').catch(function () { return []; }),
    ]);

    // v136.12: matched on the template's type code (what a submitted row carries), shown by its name. The label
    // is a name Head Office typed (never translated); the status is its own text node for the Arabic catalogue.
    const COLOURS = { ok: 'var(--ok)', bad: 'var(--ui-bad)', neutral: 'var(--sub)' };
    const lines = checklistWeekLines(templates, checklists);
    const body = lines.length
      ? lines.map(function (l) {
          return '<li style="margin:2px 0;color:' + COLOURS[l.state] + ';">' +
            '<span translate="no">' + escapeHtml(l.label) + '</span>: <span>' + escapeHtml(l.status) + '</span></li>';
        }).join('')
      : '<li style="color:var(--ui-bad);">No checklists submitted this week.</li>';

    container.innerHTML = summaryBox(
      '<strong>Checklists (week of ' + escapeHtml(week) + ')</strong>' +
      '<ul style="margin:8px 0 0;padding-left:20px;">' + body + '</ul>'
    );
  } catch (e) {
    container.innerHTML = errorBox(e.message);
  }
}


// ──────────────────────────────────────────────────────────────────
// Widget: clockIns — today's clock-in status
// ──────────────────────────────────────────────────────────────────

async function renderClockInsEmbed(store, container) {
  container.innerHTML = loadingBox();
  try {
    const today = dubaiToday();
    const [clockRows, staff] = await Promise.all([
      apiGet('/api/clock?store=' + encodeURIComponent(store) + '&date=' + today),
      getStaff(),
    ]);

    const staffById = {};
    staff.forEach(function (s) { staffById[s.id] = s; });

    const lines = (clockRows || []).map(function (c) {
      // NOTE: clock_log fields — clock_in, clock_out, staff_id per schema.
      const s = staffById[c.staff_id];
      const name = s ? s.name : ('staff #' + c.staff_id);
      let status, colour;
      if (c.clock_in && !c.clock_out) { status = 'in (' + c.clock_in + ')'; colour = 'var(--ok)'; }
      else if (c.clock_in && c.clock_out) { status = c.clock_in + ' → ' + c.clock_out; colour = 'var(--sub)'; }
      else { status = 'no clock-in'; colour = 'var(--ui-bad)'; }
      return '<li style="margin:2px 0;color:' + colour + ';">' +
        escapeHtml(name) + ' — ' + escapeHtml(status) + '</li>';
    }).join('');

    container.innerHTML = summaryBox(
      '<strong>Clock-ins today (' + escapeHtml(today) + ')</strong>' +
      (clockRows && clockRows.length
        ? '<ul style="margin:8px 0 0;padding-left:20px;">' + lines + '</ul>'
        : '<br><span style="color:var(--ui-bad);">No clock-ins recorded today.</span>')
    );
  } catch (e) {
    container.innerHTML = errorBox(e.message);
  }
}


// ──────────────────────────────────────────────────────────────────
// Widget: toilBalances — staff TOIL balances at the store
// ──────────────────────────────────────────────────────────────────

async function renderToilEmbed(store, container) {
  container.innerHTML = loadingBox();
  try {
    const year = String(new Date().getFullYear());
    const [balances, staff] = await Promise.all([
      apiGet('/api/balances?year=' + year),
      getStaff(),
    ]);

    // Balances are returned for ALL staff — filter to this store.
    const storeStaffIds = new Set(
      staff.filter(function (s) { return s.store === store; }).map(function (s) { return s.id; })
    );
    const staffById = {};
    staff.forEach(function (s) { staffById[s.id] = s; });

    const storeBalances = (balances || []).filter(function (b) {
      return storeStaffIds.has(b.staff_id);
    });

    const lines = storeBalances.map(function (b) {
      const s = staffById[b.staff_id];
      const name = s ? s.name : ('staff #' + b.staff_id);
      // NOTE: balances response field — 'toil_balance' (hours) per worker comment.
      const toil = (b.toil_balance != null) ? b.toil_balance : 0;
      const colour = toil < 0 ? 'var(--ui-bad)' : (toil >= 24 ? 'var(--ui-warn)' : 'var(--ok)');
      return '<li style="margin:2px 0;color:' + colour + ';">' +
        escapeHtml(name) + ': ' + toil + 'h TOIL</li>';
    }).join('');

    container.innerHTML = summaryBox(
      '<strong>TOIL balances (' + escapeHtml(year) + ')</strong>' +
      (storeBalances.length
        ? '<ul style="margin:8px 0 0;padding-left:20px;">' + lines + '</ul>' +
          '<div style="margin-top:6px;font-size:12px;color:var(--tiny);">Amber ≥ 24h, red if negative.</div>'
        : '<br><span style="color:var(--tiny);">No balance data for this store.</span>')
    );
  } catch (e) {
    container.innerHTML = errorBox(e.message);
  }
}


// ──────────────────────────────────────────────────────────────────
// Dispatcher — called by form-detailed.js for each shift-embed question
// ──────────────────────────────────────────────────────────────────

function renderShiftEmbed(embedKey, store, container) {
  if (!store) {
    container.innerHTML = errorBox('No store selected — go back to Setup and pick a store first.');
    return;
  }
  switch (embedKey) {
    case 'rota':         return renderRotaEmbed(store, container);
    case 'checklists':   return renderChecklistsEmbed(store, container);
    case 'clockIns':     return renderClockInsEmbed(store, container);
    case 'toilBalances': return renderToilEmbed(store, container);
    default:
      container.innerHTML = errorBox('Unknown embed: ' + escapeHtml(embedKey));
  }
}

// Reset the staff cache (call when the form opens fresh, in case staff changed)
function resetShiftEmbedCache() {
  _staffCache = null;
}

// Window re-exposure (per ELC Shift module convention)
window.renderShiftEmbed = renderShiftEmbed;
window.resetShiftEmbedCache = resetShiftEmbedCache;

export {
  renderShiftEmbed,
  resetShiftEmbedCache,
  renderRotaEmbed,
  renderChecklistsEmbed,
  renderClockInsEmbed,
  renderToilEmbed,
  checklistWeekLines,   // v136.12: pure, for the tests
  storeWeekStart,
  isQatarVisitStore,
};
